import { DataTable } from '@components/DataTable';
import { PageSection } from '@components/PageSection/PageSection';
import { ResponsiveButton } from '@components/ResponsiveButton';
import { useCurrentProject } from '@modules/projects/utils/useCurrentProject';
import { Add } from '@mui/icons-material';
import { DataGridProProps, GridColDef, GridRowEditStopReasons, GridRowOrderChangeParams, ReorderValidationContext, useGridApiRef } from '@mui/x-data-grid-pro';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { GroupingColumn } from './components/GroupingColumn';
import { BudgetTableRow } from './types';
import { useBudgetTableContext } from './context/useBudgetTableContext';
import { useBudgetTableColumns } from './hooks/useBudgetTableColumns';
import { useBudgetRowUpdater } from './hooks/useBudgetRowUpdater';
import { useBudgetTableActions } from './hooks/useBudgetTableActions';
import { useBudgetReorderMutations } from './hooks/useBudgetReorderMutations';
import { useBudgetRows } from './hooks/useBudgetRows';
import { CostItemEntryDialog } from './components/CostItemEntryDialog';

interface Props extends Omit<DataGridProProps<BudgetTableRow>, 'rows' | 'columns'> {}

const commonColumnOptions: Partial<GridColDef<BudgetTableRow>> = {
  sortable: false,
  disableColumnMenu: true,
  disableExport: true,
  disableReorder: true,
  editable: true,
};

export const BudgetItemTable: React.FC<Props> = ({ ...dataTableProps }) => {
  const { formatMessage } = useIntl();
  const { isArchived, canManageBudgetAndExpenses, projectId } = useCurrentProject();
  const { entryDialog, setEntryDialog } = useBudgetTableContext();
  const { isPending } = useBudgetTableActions();
  const apiRef = useGridApiRef();
  const reorderMutations = useBudgetReorderMutations();

  const {
    rows,
    budgetItems,
    isFetching: isFetchingBudgetItems,
    addBudgetDraft,
    addCostDraft,
    removeDraftBudgetRow,
    removeDraftCostRow,
  } = useBudgetRows(projectId);

  const { processRowUpdate } = useBudgetRowUpdater({ removeDraftBudgetRow, removeDraftCostRow });

  const allowEntryTypeSwitch = entryDialog.open
    ? !entryDialog.expense && !entryDialog.quote
    : false;

  const handleEntryTypeChange = useCallback((type: 'expense' | 'quote') => {
    setEntryDialog(previous => {
      if (!previous.open) return previous;
      return { ...previous, type };
    });
  }, [setEntryDialog]);

  const focusRowNameField = useCallback((rowId: string) => {
    setTimeout(() => apiRef.current?.startRowEditMode({ id: rowId, fieldToFocus: 'name' }), 0);
  }, [apiRef]);

  const handleAddBudgetDraft = useCallback(() => {
    const draftId = addBudgetDraft();
    focusRowNameField(draftId);
  }, [addBudgetDraft, focusRowNameField]);

  const handleAddCostDraft = useCallback((budgetId?: number) => {
    if (budgetId == null) return;
    const draftId = addCostDraft(budgetId);
    focusRowNameField(draftId);
  }, [addCostDraft, focusRowNameField]);

  const columns = useBudgetTableColumns({
    isProjectArchived: isArchived ?? false,
    budgetItems,
    apiRef,
    onAddCostItem: handleAddCostDraft,
  });

  const handleRowOrderChange = useCallback((params: GridRowOrderChangeParams) => {
    const movedRow = params.row as BudgetTableRow;
    if (movedRow.type === 'budget') {
      reorderMutations.reorderBudgetItem.mutate({
        input: {
          projectId: projectId ?? 0,
          budgetItemId: movedRow.originalId,
          index: params.targetIndex,
        },
      });
      return;
    }

    if (movedRow.type === 'cost') {
      const parentBudgetItemId = params.newParent?.toString().split('-')[1];
      if (!parentBudgetItemId) return;
      reorderMutations.reorderCostItem.mutate({
        input: {
          projectId: projectId ?? 0,
          costItemId: movedRow.originalId,
          index: params.targetIndex,
        }
      });
      return;
    }

    if (movedRow.type === 'quote' || movedRow.type === 'expense') {
      if (!movedRow.entryId) return;
      const parentCostItemId = params.newParent?.toString().split('-')[1];
      if (!parentCostItemId) return;

      reorderMutations.reorderCostItemEntry.mutate({
        input: {
          projectId: projectId ?? 0,
          costItemEntryId: movedRow.entryId,
          index: params.targetIndex,
        }
      });
      return;
    }
  }, [projectId, reorderMutations]);

  const setTreePath = useCallback((path: string[], row: BudgetTableRow) => {
    if (row.type === 'cost') {
      return { ...row, budgetItemId: Number(path[0]), treePath: [path[0], row.id] };
    }

    if (row.type === 'expense') {
      if (path.length === 4) {
        const quoteEntry = rows.find(r => r.type === 'quote' && r.entryId === Number(path[2]));
        if (!quoteEntry) return row;
        return { ...row, budgetItemId: Number(path[0]), costItemId: Number(path[1]), quoteId: quoteEntry.originalId, treePath: [path[0], path[1], path[2], row.entryId?.toString() ?? ''] };
      }
      if (path.length === 3) {
        return { ...row, budgetItemId: Number(path[0]), costItemId: Number(path[1]), quoteId: undefined, treePath: [path[0], path[1], row.entryId?.toString() ?? ''] };
      }
    }

    if (row.type === 'quote') {
      return { ...row, budgetItemId: Number(path[0]), costItemId: Number(path[1]), treePath: [path[0], path[1], row.entryId?.toString() ?? ''] };
    }

    return row;
  }, [rows]);
 
  const isValidRowReorder = useCallback((context: ReorderValidationContext) => {
    const movedRow = context.apiRef.current.getRow(context.sourceNode.id) as BudgetTableRow;

    if (movedRow.type === 'budget' && context.targetNode.parent === 'auto-generated-group-node-root' && context.dropPosition !=='inside') {
      return true;
    }

    if (movedRow.type === 'cost' && 
      ((context.targetNode.id?.toString().startsWith('cost-') && context.dropPosition !=='inside')
      || (context.targetNode.id?.toString().startsWith('budget-') && context.dropPosition ==='inside'))) {
      return true;
    }

    if (movedRow.type === 'quote' &&
      (context.targetNode.parent?.toString().startsWith('cost-') || context.targetNode.id?.toString().startsWith('cost-'))) {
      return true;
    }

    if (movedRow.type === 'expense' &&
      (context.targetNode.id?.toString().startsWith('cost-') ||
       (context.targetNode.id?.toString().startsWith('expense-') && context.dropPosition !=='inside')
       
       || context.targetNode.id?.toString().startsWith('quote-'))) {
      return true;
    }

    return false;
  }, []);

const groupingColDef: DataGridProProps['groupingColDef'] = {
  headerName: '',
  renderCell: GroupingColumn as any,
  width: 70,

};

  return (
    <PageSection
      title={formatMessage({ id: 'Budget items' })}
      actionButton={!isArchived && canManageBudgetAndExpenses && (

        <ResponsiveButton
          loading={isPending}
          onClick={handleAddBudgetDraft}
          variant='contained' color='primary' icon={<Add />}>
          {formatMessage({ id: 'Add budget item' })}
        </ResponsiveButton>
      )}
    >
      <DataTable<BudgetTableRow>
        apiRef={apiRef}
        editMode='row'
        rowReordering={canManageBudgetAndExpenses ? true : false}
        isCellEditable={({ field, row }) => 
          canManageBudgetAndExpenses 
          && !isArchived && 
          (
            (row.type === 'cost' && (field === 'name' || field === 'planned' || field === 'status'))
            || (row.type === 'budget' && (field === 'name' || field === 'planned'))
          )
        }
        loading={isPending || isFetchingBudgetItems}
        onCellClick={cell => {
          if (!canManageBudgetAndExpenses) return;
          if (cell.colDef.field === '__tree_data_group__') return;

          if (cell.row.type === 'quote') {
            setEntryDialog({
              open: true,
              costItemId: cell.row.costItemId,
              type: 'quote',
              quote: cell.row.type === 'quote' ? cell.row.quote : undefined,
            });
            return;
          }
          if (cell.row.type === 'expense' && cell.row.expense) {
            setEntryDialog({
              open: true,
              costItemId: cell.row.costItemId,
              type: 'expense',
              expense: cell.row.expense,
            });
            return;
          }
          if (!cell.isEditable || apiRef.current?.getCellMode(cell.row.id, cell.colDef.field) == 'edit') return;

          apiRef.current?.startRowEditMode({ fieldToFocus: cell.colDef.field, id: cell.row.id });
        }}
        onRowEditStop={(params) => {
          if (params.reason === GridRowEditStopReasons.rowFocusOut) {
            apiRef.current?.stopRowEditMode({ id: params.id, ignoreModifications: false });
          }
          if (params.reason === GridRowEditStopReasons.escapeKeyDown) {
            const row = apiRef.current?.getRow(params.id) as BudgetTableRow | undefined;
            if (row?.originalId === -1) {
              if (row.type === 'budget') {
                removeDraftBudgetRow(row.id);
              } else if (row.type === 'cost') {
                removeDraftCostRow(row.budgetItemId, row.id);
              }
            }
          }
        }}
        onRowOrderChange={handleRowOrderChange}
        isValidRowReorder={isValidRowReorder}
        setTreeDataPath={setTreePath}
        groupingColDef={groupingColDef}
        processRowUpdate={processRowUpdate}
        treeData
        getTreeDataPath={(row) => row.treePath} 
        defaultGroupingExpansionDepth={3}
        columns={columns.map((c) => ({ ...commonColumnOptions, ...c })) as GridColDef<BudgetTableRow>[]}
        rows={rows}
        noDataMessage={formatMessage({ id: 'No budget items.' })}
        {...dataTableProps}
      />


      <CostItemEntryDialog
        open={entryDialog.open}
        onClose={() => setEntryDialog({ open: false })}
        costItemId={entryDialog.open ? entryDialog.costItemId : undefined}
        type={entryDialog.open ? entryDialog.type : 'expense'}
        expense={entryDialog.open ? entryDialog.expense : undefined}
        quote={entryDialog.open ? entryDialog.quote : undefined}
        allowTypeSwitch={allowEntryTypeSwitch}
        onTypeChange={handleEntryTypeChange}
      />

    </PageSection>
  );
};